import { useCallback, useMemo } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import { fillArray, sortBy, unique } from 'lib/array/arrayUtils'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import focusStyle from 'styles/utils/focusStyle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useIntl } from 'react-intl'

const PaginationButton = styled(Clickable)`
  height: ${rem(32)};
  width: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, colour 0.2s;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      color: ${props => props.theme.palette.neutral.surface.lightContrast};
    }
  }

  &:focus-visible {
    ${focusStyle}
  }

  &.selected {
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }
`

interface Props extends React.PropsWithChildren {
  label: string
  total: number
  currentPage: number
  pageSize: number
  onPageChange?: (page: number) => void
}

function UserReviewPagination(props: Props) {
  const intl = useIntl()
  const { children, label, total, currentPage, pageSize, onPageChange } = props

  const pageButtons = useMemo(() => {
    const pageCount = Math.ceil(total / pageSize)

    if (pageCount <= 3) {
      return fillArray(pageCount).map((page, index) => index + 1)
    }

    // always have the first and last page plus
    // the page either side of the current page
    const uniquePages = unique([1, currentPage - 1, currentPage, currentPage + 1, pageCount])
    const pages = sortBy(uniquePages, (page) => page, 'asc')
    // now insert separators where numbers aren't contiguous
    return pages.reduce<Array<number | string>>((acc, page) => {
      if (page > pageCount || page < 1) {
        // can't have more pages than the maximum
        return acc
      }
      const previous = acc.at(-1)
      if (typeof previous === 'number' && (previous + 1) !== page) {
        // found a gap, add a separator
        acc.push('...')
      }
      acc.push(page)

      return acc
    }, [])
  }, [currentPage, pageSize, total])

  const onChangePage = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const page = event.currentTarget.dataset.page
    if (page) {
      onPageChange?.(parseInt(page, 10))
    }
  }, [onPageChange])

  return <VerticalSpacer gap={24}>
    <div>
      {children}
    </div>
    <Group
      direction="horizontal"
      horizontalAlign="space-between"
      verticalAlign="center"
    >
      <Caption variant="medium">
        {intl.formatMessage(
          {
            id: 'zdye_TrB-qHC2UGDeyWC',
            defaultMessage: '{label}: {rangeStart}-{rangeEnd} of {total}',
            description: 'Pagination range text showing current range and total for user reviews',
          },
          {
            label,
            rangeStart: ((currentPage - 1) * pageSize) + 1,
            rangeEnd: Math.min(currentPage * pageSize, total),
            total,
          },
        )}
      </Caption>
      {!!pageButtons.length && (
        <Group direction="horizontal" verticalAlign="center" gap={8}>
          {pageButtons.map((option, index) => <React.Fragment key={index}>
            {typeof option === 'number' && <PaginationButton
              data-page={option}
              onClick={onChangePage}
              className={clsx({ selected: currentPage === option })}
            >
              <BodyText variant="small">
                {option}
              </BodyText>
            </PaginationButton>}
            {typeof option === 'string' && <BodyText variant="small" colour="light-foreground-alt">
              {option}
            </BodyText>}
          </React.Fragment>)}
        </Group>
      )}
    </Group>
  </VerticalSpacer>
}

export default UserReviewPagination
